/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal System Enums and Constants
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

/**
 * System-wide user authentication and privilege levels.
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  USER = 'user',
}

/**
 * Workspace multi-tenancy membership roles.
 */
export enum WorkspaceRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

/**
 * Asynchronous background AI generation job statuses.
 */
export enum AiJobStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/**
 * Immutable credit ledger transaction action types.
 */
export enum WalletActionType {
  SUBSCRIPTION_GRANT = 'SUBSCRIPTION_GRANT',
  TOPUP_PURCHASE = 'TOPUP_PURCHASE',
  USAGE_DEDUCTION = 'USAGE_DEDUCTION',
  REFUND = 'REFUND',
  ADMIN_ADJUSTMENT = 'ADMIN_ADJUSTMENT',
}

/**
 * Supported payment gateway identifiers.
 */
export enum GatewayCode {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  PAYSTACK = 'paystack',
  RAZORPAY = 'razorpay',
  CRYPTO = 'crypto',
  MANUAL_BANK = 'bank_transfer',
}

/**
 * AI Model operational modalities.
 */
export enum AiModelType {
  TEXT = 'text',
  CHAT = 'chat',
  IMAGE = 'image',
  VIDEO = 'video',
  TTS = 'speech',
  STT = 'stt',
  EMBEDDING = 'embedding',
}

/**
 * Subscription lifecycle statuses.
 */
export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  PAST_DUE = 'past_due',
  TRIALING = 'trialing',
}

/**
 * Financial invoice / order payment statuses.
 */
export enum OrderStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

/**
 * R2 stored media asset categories.
 */
export enum MediaAssetType {
  IMAGE = 'image',
  AUDIO_TTS = 'audio_tts',
  AUDIO_STT = 'audio_stt',
  AUDIO_VOICE_CLONE = 'audio_voice_clone',
  AUDIO_SAMPLE = 'audio_sample',
  AUDIO_MIX = 'audio_mix',
  VIDEO = 'video',
}

/**
 * CAT translation project progress states.
 */
export enum TranslationStatus {
  DRAFT = 'draft',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
}

/**
 * Computer-Assisted Translation (CAT) specialized transcreation and tone of voice registers (Task 8.3.3).
 */
export enum TranscreationTone {
  PROFESSIONAL = 'professional',
  MARKETING = 'marketing',
  CASUAL = 'casual',
  TECHNICAL = 'technical',
}

